import React, { useEffect, useState } from "react";
import type { FC, SVGProps, JSX } from "react";

import cn from "classnames";

import { Icons } from "../../assets/types/icons";

import type { TIconSizes } from "../../assets/types/icons";
import type { PropsWithClassName, PropsWithTsId } from "../../types/react";

import classes from "./Icon.styl";

export const ICON_TEST_ID = "icon";
export const ICON_STUB_TEST_ID = "stub-test-id";
const ICON_DISPLAY_NAME = "Icon";

export type IconProps = PropsWithClassName<TIconSizes> & Partial<PropsWithTsId>;

/**
 * Компонент для установки иконок из нашей библиотеки.
 *
 * <a href="https://www.figma.com/design/vTg3KAvmAXA9LigeyiqCWL/%F0%9F%92%A0-Base-Components?m=auto&node-id=2-98752" target="_blank">Спека</a>
 * @constructor
 */
export const Icon: FC<IconProps> = ({
    className,
    icon,
    size,
    "data-tsid": tsid,
}): JSX.Element | null => {
    const [{ IconComponent }, setIconComponent] = useState<{
        IconComponent: FC;
    }>({
        IconComponent: Stub,
    });

    const iconPath = (Icons[size] as Record<string, string>)?.[icon];

    if (!iconPath) {
        throw new Error(
            `Иконки с именем ${icon} и размером ${size} не существует!`,
        );
    }

    const svgProps: SVGProps<SVGSVGElement> = {
        className: cn(classes["icon"], className),
        width: size,
        height: size,
        "aria-hidden": true,
    };

    useEffect(() => {
        import(`../../assets/icons/${iconPath}`)
            .then(({ default: Component }) => {
                setIconComponent({
                    IconComponent: Component,
                });
            })
            .catch((e) => {
                throw new Error(
                    `Иконки с именем ${icon} и размером ${size} не существует! ${e}`,
                );
            });
    }, [iconPath, icon, size]);

    return (
        <IconComponent
            {...svgProps}
            data-tsid={tsid || ICON_TEST_ID}
            data-uikit={ICON_DISPLAY_NAME}
        />
    );
};

interface IStub extends SVGProps<SVGSVGElement> {}

/**
 * Показываем эту заглушку, пока грузится основная иконка
 * @param props
 * @constructor
 */
const Stub: FC<IStub> = (props) => {
    // eslint-disable-next-line ok-custom-rules/check-attribute
    return <svg {...props} data-tsid={ICON_STUB_TEST_ID} />;
};

Icon.displayName = ICON_DISPLAY_NAME;

export default Icon;
